import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, Subject } from 'rxjs';
import { AuthService } from '../../../../../../services/auth.service';
import { CampaignsService } from '../../../../../../services/api/generated/campaigns/campaigns.service';

export interface CampaignLogTexts {
  comment: string;
  oldStatusLabel?: string;
  newStatusLabel?: string;
  statusFieldLabel?: string;
}
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { InfoStore } from '../../../../../../stores/info.store';
import { LogCreationData, LogDetailCreationData, LogStore } from '../../../../../../stores/log.store';
import { UserStore } from '../../../../../../stores/user.store';
import { Campaign, CampaignStatus } from '../../../../../../types/campaign.interface';
import { CAMPAIGN_STATUS_I18N_KEYS } from '../../../../../../types/campaign.utils';
import { InfoType } from '../../../../../../types/info.interface';
import { Log, LogDetailChangeType } from '../../../../../../types/log.interface';

export interface CampaignLogEntry extends Log {
  entityName?: string;
  actionType?: string;
  formattedDate?: string;
}

export interface CampaignActionContext {
  campaignId: number;
  campaignName?: string;
  assetId?: number;
  userId?: number;
}

export interface AuscultationModificationDetail {
  infoId: number;
  infoName?: string;
  infoType: InfoType;
  attribute: string;
  oldValue: string | null;
  newValue: string | null;
  type: 'update' | 'create' | 'delete';
}

export interface MeasureModificationDetail {
  measureId: number;
  measureName: string;
  inputDataId?: number;
  oldValue: string | null;
  newValue: string | null;
  type: 'update' | 'create';
}

export interface PlanningModificationDetail {
  itemType: 'measure' | 'auscultation';
  itemId: number;
  itemName: string;
  action: 'plan' | 'unplan';
}

@Injectable({
  providedIn: 'root',
})
export class CampaignHistoryService {
  readonly logStore = inject(LogStore);
  private readonly authService = inject(AuthService);
  private readonly campaignsApi = inject(CampaignsService);
  private readonly campaignStore = inject(CampaignStore);
  private readonly infoStore = inject(InfoStore);
  private readonly userStore = inject(UserStore);
  private readonly translate = inject(TranslocoService);

  private readonly _currentCampaignId = signal<number | null>(null);
  private readonly _isLoading = signal(false);
  private readonly _listMutations = new Subject<{ campaignId: number; reason: 'status' | 'delete' }>();

  readonly currentCampaignId = this._currentCampaignId.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly listMutations$ = this._listMutations.asObservable();

  readonly campaignHistory = computed<CampaignLogEntry[]>(() => {
    const campaignId = this._currentCampaignId();
    if (!campaignId) return [];

    const allLogs = this.logStore.entities();

    const logs = allLogs.filter((log: Log) => log.entity === 'campaign' && log.entityId === campaignId);

    const mappedLogs = logs.map((log: Log) => {
      const mapped = {
        ...log,
        entityName: this.getEntityDisplayName(log.entity),
        actionType: this.getActionType(log),
        formattedDate: this.formatLogDate(log.date),
      };
      return mapped;
    });

    return mappedLogs;
  });

  setCampaignId(campaignId: number | null): void {
    this._currentCampaignId.set(campaignId);
  }

  async loadCampaignHistory(campaignId: number): Promise<Log[]> {
    this.setCampaignId(campaignId);
    return this.logStore.loadByEntity('campaign', campaignId);
  }

  currentUserId(): number | null {
    return this.authService.currentUserId() ?? null;
  }

  private getEntityDisplayName(entity: string): string {
    const entityKeyMap: Record<string, string> = {
      'campaign': 'history.entities.campaign',
      'campaign-measure': 'history.entities.campaignMeasure',
      'campaign-info': 'history.entities.campaignInfo',
      'measure': 'history.entities.measure',
      'info': 'history.entities.info',
      'input-data': 'history.entities.inputData',
      'file': 'history.entities.file',
    };

    const key = entityKeyMap[entity];
    return key ? this.translate.translate(key) : entity;
  }

  private readonly ACTION_KEY_MAP: Record<string, string> = {
    'history.comments.creation': 'creation',
    'history.comments.updateGeneral': 'update',
    'history.comments.updateMeasures': 'measures',
    'history.comments.updateAuscultation': 'auscultation',
    'history.comments.updatePlanning': 'planning',
    'history.comments.planningAdded': 'planning',
    'history.comments.planningRemoved': 'planning',
    'history.comments.deletion': 'deletion',
  };

  private getActionType(log: Log): string {
    const comment = log.comment ?? '';
    return this.ACTION_KEY_MAP[comment] ?? 'other';
  }

  formatLogDate(date: string): string {
    return new Date(date).toLocaleString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private getCampaignName(campaignId: number): string {
    const campaign = this.campaignStore.byId()(campaignId);
    if (campaign?.timestamp) {
      return this.formatLogDate(campaign.timestamp);
    }
    return `Campaign #${campaignId}`;
  }

  private getInfoName(infoId: number): string {
    const info = this.infoStore.byId()(infoId);
    const name = info?.name;
    return name && name.length > 0 ? name : `Info #${infoId}`;
  }

  private getParentEntityName(campaignId: number): string {
    const campaignName = this.getCampaignName(campaignId);
    return this.translate.translate('history.parentEntity', { name: campaignName, id: campaignId });
  }

  async logCampaignUpdate(context: CampaignActionContext, updates: Partial<Campaign>, previousData?: Partial<Campaign>): Promise<Log> {
    const attributeLabels: Record<string, string> = {
      name: 'history.fields.name',
      comment: 'history.fields.comment',
      operator: 'history.fields.operator',
      date: 'history.fields.date',
    };

    const changedKeys = Object.keys(updates).filter(key => {
      const oldValue = previousData?.[key as keyof Campaign];
      const newValue = updates[key as keyof Campaign];
      return oldValue !== newValue;
    });

    const comment = 'history.comments.updateGeneral';
    const parentEntityName = this.getParentEntityName(context.campaignId);

    const details: LogDetailCreationData[] = changedKeys.map(key => {
      const oldValue = previousData?.[key as keyof Campaign];
      const newValue = updates[key as keyof Campaign];
      return {
        entity: 'campaign' as const,
        entityId: context.campaignId,
        attribute: key,
        oldValue: oldValue ? String(oldValue) : null,
        newValue: newValue ? String(newValue) : null,
        type: LogDetailChangeType.UPDATE,
        entityName: attributeLabels[key] ?? key,
        parentEntityName,
      };
    });

    const logData: LogCreationData = {
      entity: 'campaign',
      entityId: context.campaignId,
      comment,
      userId: context.userId ?? this.currentUserId(),
    };

    return this.logStore.createLogWithDetails(logData, details);
  }

  async logCampaignDeletion(context: CampaignActionContext): Promise<Log> {
    const comment = 'history.comments.deletion';
    const parentEntityName = this.getParentEntityName(context.campaignId);

    const logData: LogCreationData = {
      entity: 'campaign',
      entityId: context.campaignId,
      comment,
      userId: context.userId ?? this.currentUserId(),
    };

    const details: LogDetailCreationData[] = [
      {
        entity: 'campaign',
        entityId: context.campaignId,
        attribute: 'campaign',
        oldValue: context.campaignName && context.campaignName.length > 0 ? context.campaignName : `Campaign #${context.campaignId}`,
        newValue: null,
        type: LogDetailChangeType.DELETE,
        entityName: context.campaignName && context.campaignName.length > 0 ? context.campaignName : `Campaign #${context.campaignId}`,
        parentEntityName,
      },
    ];

    return this.logStore.createLogWithDetails(logData, details);
  }

  async logMultipleAuscultationUpdate(context: CampaignActionContext, modifications: AuscultationModificationDetail[]): Promise<Log> {
    const comment = 'history.comments.updateAuscultation';
    const parentEntityName = this.getParentEntityName(context.campaignId);

    const logData: LogCreationData = {
      entity: 'campaign',
      entityId: context.campaignId,
      comment,
      userId: context.userId ?? this.currentUserId(),
    };

    const details: LogDetailCreationData[] = modifications.map(mod => {
      const infoName = mod.infoName && mod.infoName.length > 0 ? mod.infoName : this.getInfoName(mod.infoId);

      let changeType: LogDetailChangeType;
      switch (mod.type) {
        case 'create':
          changeType = LogDetailChangeType.CREATE;
          break;
        case 'delete':
          changeType = LogDetailChangeType.DELETE;
          break;
        default:
          changeType = LogDetailChangeType.UPDATE;
          break;
      }

      return {
        entity: 'campaign-info',
        entityId: mod.infoId,
        attribute: mod.attribute,
        oldValue: mod.oldValue,
        newValue: mod.newValue,
        type: changeType,
        entityName: infoName,
        parentEntityName,
      };
    });

    return this.logStore.createLogWithDetails(logData, details);
  }

  async logMeasuresUpdate(context: CampaignActionContext, modifications: MeasureModificationDetail[]): Promise<Log> {
    const comment = 'history.comments.updateMeasures';
    const parentEntityName = this.getParentEntityName(context.campaignId);

    const logData: LogCreationData = {
      entity: 'campaign',
      entityId: context.campaignId,
      comment,
      userId: context.userId ?? this.currentUserId(),
    };

    const details: LogDetailCreationData[] = modifications.map(mod => ({
      entity: 'campaign-measure',
      entityId: mod.inputDataId ?? mod.measureId,
      attribute: 'value',
      oldValue: mod.oldValue,
      newValue: mod.newValue,
      type: mod.type === 'create' ? LogDetailChangeType.CREATE : LogDetailChangeType.UPDATE,
      entityName: mod.measureName,
      parentEntityName,
    }));

    return this.logStore.createLogWithDetails(logData, details);
  }

  async logPlanningUpdate(context: CampaignActionContext, modifications: PlanningModificationDetail[]): Promise<Log> {
    const isPlan = modifications.some(m => m.action === 'plan');
    const isUnplan = modifications.some(m => m.action === 'unplan');

    let comment = 'history.comments.updatePlanning';
    if (isPlan && !isUnplan) comment = 'history.comments.planningAdded';
    else if (!isPlan && isUnplan) comment = 'history.comments.planningRemoved';

    const parentEntityName = this.getParentEntityName(context.campaignId);

    const logData: LogCreationData = {
      entity: 'campaign',
      entityId: context.campaignId,
      comment,
      userId: context.userId ?? this.currentUserId(),
    };

    const details: LogDetailCreationData[] = modifications.map(mod => ({
      entity: mod.itemType === 'measure' ? 'campaign-measure' : 'campaign-info',
      entityId: mod.itemId,
      attribute: 'planned',
      oldValue: mod.action === 'plan' ? 'false' : 'true',
      newValue: mod.action === 'plan' ? 'true' : 'false',
      type: mod.action === 'plan' ? LogDetailChangeType.CREATE : LogDetailChangeType.DELETE,
      entityName: mod.itemName,
      parentEntityName,
    }));

    return this.logStore.createLogWithDetails(logData, details);
  }

  async refreshCampaignHistory(): Promise<void> {
    const campaignId = this._currentCampaignId();
    if (campaignId) {
      await this.logStore.refreshEntityLogs('campaign', campaignId);
    }
  }

  getUserName(userId: number): { firstName: string; lastName: string } | null {
    const user = this.userStore.byId()(userId);
    return user ? { firstName: user.firstname, lastName: user.name } : null;
  }

  async updateCampaignStatusWithHistory(campaignId: number, newStatus: CampaignStatus): Promise<Campaign | null> {
    const currentCampaign = this.campaignStore.byId()(campaignId);
    if (!currentCampaign) return null;

    const logTexts = this.buildStatusChangeLogTexts(currentCampaign.status, newStatus);

    const updatedCampaign = await firstValueFrom(
      this.campaignsApi.putCampaignDetailResource<Campaign>({ id: campaignId }, { status: newStatus, logTexts }),
    );

    this.campaignStore.updateEntity(campaignId, updatedCampaign);
    this._listMutations.next({ campaignId, reason: 'status' });
    await this.logStore.refreshEntityLogs('campaign', campaignId).catch(() => {});

    return updatedCampaign;
  }

  async deleteCampaignWithHistory(campaignId: number): Promise<boolean> {
    const currentCampaign = this.campaignStore.byId()(campaignId);
    if (!currentCampaign) return false;

    const logTexts = this.buildDeletionLogTexts();
    await firstValueFrom(this.campaignsApi.deleteCampaignDetailResource<void>({ id: campaignId }, { logTexts }));

    this.campaignStore.removeEntity(campaignId);
    this._listMutations.next({ campaignId, reason: 'delete' });

    return true;
  }

  private buildStatusChangeLogTexts(oldStatus: CampaignStatus, newStatus: CampaignStatus): CampaignLogTexts {
    const oldKey = CAMPAIGN_STATUS_I18N_KEYS[oldStatus] || String(oldStatus);
    const newKey = CAMPAIGN_STATUS_I18N_KEYS[newStatus] || String(newStatus);
    return {
      comment: 'history.comments.updateGeneral',
      oldStatusLabel: oldKey,
      newStatusLabel: newKey,
      statusFieldLabel: 'common.labels.status',
    };
  }

  private buildDeletionLogTexts(): CampaignLogTexts {
    return { comment: 'history.comments.deletion' };
  }
}
